import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { Plus, Eye, Edit, Copy, Trash2, FileDown, Search, FileText, LayoutGrid, List, Check, X, TrendingUp, ArrowUpDown, ArrowUp, ArrowDown, RotateCcw, Trash, User } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getPhotoUrl } from '@/utils/photoUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const badgeVariants = {
  Borrador: 'secondary',
  Enviada: 'default',
  Adjudicada: 'outline',
};

const isVencida = (lote) => {
  if (!lote.fechaLimiteOfertas) return false;
  if (['Adjudicada', 'Borrador', 'En espera', 'No adjudicada'].includes(lote.estadoPublicacion)) return false;
  return new Date(lote.fechaLimiteOfertas) < new Date();
};

const Publicaciones = ({ isMisPublicaciones = false }) => {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'Admin' || user?.rol === 'SuperAdmin';
  const isSuperAdmin = user?.rol === 'SuperAdmin' || user?.isSuperAdmin;
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  
  const [usuarios, setUsuarios] = useState([]);
  const [changeAuthorDialog, setChangeAuthorDialog] = useState(null);
  const [selectedNewAuthor, setSelectedNewAuthor] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const serverChunkSize = 100;

  // Filtros
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'cards' o 'list' - default: list
  const [selectedLoteOfertas, setSelectedLoteOfertas] = useState(null);
  const [ofertas, setOfertas] = useState([]);
  const [clientes, setClientes] = useState({});
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [permanentDeleteId, setPermanentDeleteId] = useState(null);
  const [processingOffer, setProcessingOffer] = useState(null);

  // Load dictionaries on mount
  useEffect(() => {
    loadDictionaries();
  }, []);

  // Debounce search input
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 600);
    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  // Reload chunk 0 when filters change
  useEffect(() => {
    setCurrentPage(1);
    loadLotesChunk(0);
  }, [searchTerm, estadoFilter, sortField, sortDirection, isMisPublicaciones]);

  // Automatically fetch next chunk if user paginates beyond local cache bounds
  useEffect(() => {
    const requiredStartIndex = (currentPage - 1) * pageSize;
    if (requiredStartIndex >= lotes.length && lotes.length < totalCount) {
      loadLotesChunk(lotes.length);
    }
  }, [currentPage]);

  const loadDictionaries = async () => {
    try {
      const promises = [
        axios.get(`${API}/clientes?includeDeleted=true`)
      ];
      if (isSuperAdmin) {
        promises.push(axios.get(`${API}/usuarios`));
      }
      const results = await Promise.all(promises);
      
      const revMap = {};
      results[0].data.forEach(r => { revMap[r.id] = r; });
      setClientes(revMap);
      
      if (isSuperAdmin && results.length > 1) {
        setUsuarios(results[1].data);
      }
    } catch (e) {
      console.error('Error dictionaries:', e);
    }
  };

  const loadLotesChunk = async (skip) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/lotes/paginated`, {
        params: {
          skip,
          limit: serverChunkSize,
          searchTerm: searchTerm || undefined,
          estadoFilter: estadoFilter !== 'all' ? estadoFilter : undefined,
          sortField,
          sortDirection,
          misPublicaciones: isMisPublicaciones
        }
      });

      const { items, total } = res.data;
      setTotalCount(total);

      if (skip === 0) {
        setLotes(items);
      } else {
        setLotes(prev => {
          // avoid duplicates if React Strict Mode double-fires
          const newIds = items.map(i => i.id);
          const filteredPrev = prev.filter(p => !newIds.includes(p.id));
          return [...filteredPrev, ...items];
        });
      }
    } catch (error) {
      console.error('Error loading lotes chunk:', error);
      toast.error('Error al cargar publicaciones');
    } finally {
      setLoading(false);
    }
  };

  const refreshFullState = () => {
    loadDictionaries();
    loadLotesChunk(0);
  };

  const handleViewOfertas = async (lote) => {
    setSelectedLoteOfertas(lote);
    try {
      const res = await axios.get(`${API}/ofertas/lote/${lote.id}`);
      setOfertas(res.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Error al cargar ofertas del vehículo');
    }
  };

  const handleAcceptOffer = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Aceptada' });
      toast.success('Oferta aceptada');
      refreshFullState();
      const updatedLote = await axios.get(`${API}/lotes/${selectedLoteOfertas.id}`);
      setSelectedLoteOfertas(updatedLote.data);
      const res = await axios.get(`${API}/ofertas/lote/${selectedLoteOfertas.id}`);
      setOfertas(res.data);
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error('Error al aceptar oferta');
    } finally {
      setProcessingOffer(null);
    }
  };

  const handleRejectOffer = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Rechazada' });
      toast.success('Oferta rechazada');
      refreshFullState();
      const res = await axios.get(`${API}/ofertas/lote/${selectedLoteOfertas.id}`);
      setOfertas(res.data);
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error('Error al rechazar oferta');
    } finally {
      setProcessingOffer(null);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/lotes/${id}`);
      toast.success('Publicación movida a papelera');
      refreshFullState();
    } catch (error) {
      console.error('Error deleting lote:', error);
      toast.error('Error al eliminar publicación');
    }
    setDeleteId(null);
  };

  const handleRestore = async (id) => {
    try {
      await axios.patch(`${API}/lotes/${id}/restore`);
      toast.success('Publicación restaurada');
      refreshFullState();
    } catch (error) {
      console.error('Error restoring lote:', error);
      toast.error('Error al restaurar publicación');
    }
  };

  const handlePermanentDelete = async (id) => {
    try {
      await axios.delete(`${API}/lotes/${id}/permanent`);
      toast.success('Publicación eliminada definitivamente');
      refreshFullState();
    } catch (error) {
      console.error('Error permanently deleting lote:', error);
      toast.error('Error al eliminar definitivamente');
    }
    setPermanentDeleteId(null);
  };

  // Pagination bounds to display
  const currentDisplayLotes = lotes.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleChangeAuthor = async () => {
    try {
      if (!selectedNewAuthor || !changeAuthorDialog) return;
      const newAuthorUser = usuarios.find(u => u.id === selectedNewAuthor);
      if (!newAuthorUser) return;
      
      await axios.put(`${API}/lotes/${changeAuthorDialog}`, {
        autor: newAuthorUser.nombre,
        autorId: newAuthorUser.id
      });
      toast.success("Autor cambiado con éxito");
      setChangeAuthorDialog(null);
      setSelectedNewAuthor('');
      refreshFullState();
    } catch (error) {
      console.error('Error cambiando autor:', error);
      toast.error('Error al cambiar de autor');
    }
  };

  const handleExportPDF = () => {
    toast.info('Exportación PDF iniciada (demo)');
  };

  const handleExportExcel = () => {
    toast.info('Exportación Excel iniciada (demo)');
  };



  return (
    <div className="space-y-6 bg-background min-h-screen">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">
                {isMisPublicaciones ? 'Mis Publicaciones' : 'Publicaciones'}
              </h1>
              <p className="text-muted-foreground mt-1">
                {totalCount} publicaciones
              </p>
            </div>
            <div className="flex gap-2">
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="rounded-r-none"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Button asChild data-testid="new-listing-button">
                <Link to="/publicaciones/nueva">
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Publicación
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Filtros */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por tipoGanado, categoria o autor..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchTerm(searchInput);
                  }
                }}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={estadoFilter} onValueChange={setEstadoFilter}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid={isMisPublicaciones ? "estado-filter-mis" : "estado-filter"}>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="Borrador">Borrador</SelectItem>
                <SelectItem value="Enviada">Enviada</SelectItem>
                <SelectItem value="Vencida">Vencida</SelectItem>
                <SelectItem value="Adjudicada">Adjudicada</SelectItem>
                <SelectItem value="En espera">En espera</SelectItem>
                <SelectItem value="No adjudicada">No adjudicada</SelectItem>
                <SelectItem value="papelera">🗑️ Papelera</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                data-testid="export-pdf-button"
              >
                <FileDown className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                data-testid="export-excel-button"
              >
                <FileDown className="h-4 w-4 mr-2" />
                Excel
              </Button>
            </div>
          </div>
        </Card>

        {/* Lista de publicaciones */}
        {loading && lotes.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="text-muted-foreground">Cargando publicaciones...</div>
            </div>
          </Card>
        ) : estadoFilter === 'papelera' ? (
          totalCount === 0 ? (
            <Card className="p-12 text-center">
              <div className="flex flex-col items-center gap-4">
                <Trash className="h-12 w-12 text-muted-foreground" />
                <div>
                  <h3 className="text-lg font-semibold mb-1">La papelera está vacía</h3>
                  <p className="text-muted-foreground">Las publicaciones eliminadas aparecerán aquí</p>
                </div>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 font-medium">Vehículo</th>
                      <th className="text-left p-4 font-medium">Estado anterior</th>
                      <th className="text-left p-4 font-medium">Eliminado</th>
                      <th className="text-right p-4 font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentDisplayLotes.map((lote) => (
                      <tr key={lote.id} className="border-b last:border-0 hover:bg-accent/50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                              {lote.fotos && lote.fotos[0] ? (
                                <img src={getPhotoUrl(lote.fotos[0])} alt={lote.tipoGanado} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <FileText className="h-6 w-6 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-medium">{lote.tipoGanado} {lote.categoria}</div>
                              <div className="text-sm text-muted-foreground">{lote.raza} • {lote.numeroLote}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          {isVencida(lote) ? (
                            <Badge variant="destructive">
                              Vencida
                            </Badge>
                          ) : (
                            <Badge variant={badgeVariants[lote.estadoPublicacion]}>
                              {lote.estadoPublicacion}
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {new Date(lote.deletedAt).toLocaleDateString('es-UY', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleRestore(lote.id)} className="gap-2">
                              <RotateCcw className="h-4 w-4" />
                              Restaurar
                            </Button>
                            {isAdmin && (
                              <Button variant="outline" size="sm" onClick={() => setPermanentDeleteId(lote.id)} className="gap-2 text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                                Eliminar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )
        ) : totalCount === 0 ? (
          <Card className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <FileText className="h-12 w-12 text-muted-foreground" />
              <div>
                <h3 className="text-lg font-semibold mb-1">
                  {lotes.length === 0 ? 'No hay publicaciones' : 'No se encontraron resultados'}
                </h3>
                <p className="text-muted-foreground">
                  {lotes.length === 0
                    ? 'Comienza creando tu primera publicación'
                    : 'Intenta con otros filtros de búsqueda'}
                </p>
              </div>
              {lotes.length === 0 && (
                <Button asChild>
                  <Link to="/publicaciones/nueva">
                    <Plus className="h-4 w-4 mr-2" />
                    Crear Primera Publicación
                  </Link>
                </Button>
              )}
            </div>
          </Card>
        ) : viewMode === 'cards' ? (
          <div className="grid gap-4">
            {currentDisplayLotes.map((lote) => (
              <Card key={lote.id} className="p-4 hover:shadow-md transition-shadow" data-testid="lote-card">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Imagen */}
                  <div className="w-full sm:w-32 h-32 rounded-md overflow-hidden bg-muted flex-shrink-0">
                    {lote.fotos && lote.fotos[0] ? (
                      <img
                        src={getPhotoUrl(lote.fotos[0])}
                        alt={`${lote.tipoGanado} ${lote.categoria}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Información */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold">
                          {lote.tipoGanado} {lote.categoria}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {lote.raza} • {lote.numeroLote} • {lote.cantidad.toLocaleString()} cantidad
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Autor: {lote.autor}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Fin Ofertas: {lote.fechaLimiteOfertas ? new Date(lote.fechaLimiteOfertas).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {isVencida(lote) ? (
                          <Badge variant="destructive" data-testid="listing-status-badge">
                            Vencida
                          </Badge>
                        ) : (
                          <Badge variant={badgeVariants[lote.estadoPublicacion]} data-testid="listing-status-badge">
                            {lote.estadoPublicacion}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-lg font-bold text-primary">
                        {lote.precioBase ? `$${lote.precioBase.toLocaleString('en-US')}` : 'Sin Base'}
                      </span>
                      {lote.precioAdjudicado && (
                        <span className="text-sm text-muted-foreground">
                          (Adjudicado: ${lote.precioAdjudicado.toLocaleString('en-US')})
                        </span>
                      )}
                    </div>

                    {/* Acciones */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleViewOfertas(lote)}
                        className="gap-2"
                        data-testid="view-offers-card-button"
                      >
                        <TrendingUp className="h-4 w-4" />
                        Ofertas ({lote.ofertasCount || 0})
                      </Button>
                      <Button asChild variant="outline" size="sm" data-testid="view-detail-button">
                        <Link to={`/publicaciones/${lote.id}`}>
                          <Eye className="h-4 w-4 mr-2" />
                          Ver Detalle
                        </Link>
                      </Button>
                      {lote.estadoPublicacion === 'Borrador' && (
                        <Button asChild variant="outline" size="sm">
                          <Link to={`/publicaciones/nueva?edit=${lote.id}`}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </Link>
                        </Button>
                      )}
                      {isSuperAdmin && (
                        <Button variant="outline" size="sm" onClick={() => setChangeAuthorDialog(lote.id)}>
                          <User className="h-4 w-4 mr-2" />
                          Autor
                        </Button>
                      )}
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/publicaciones/nueva?duplicar=${lote.id}`}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicar
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteId(lote.id)}
                        data-testid="delete-button"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="border-b">
                  <tr>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('lote')}>
                      <div className="flex items-center gap-2">
                        Vehículo
                        {sortField === 'lote' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('numeroLote')}>
                      <div className="flex items-center gap-2">
                        Lote
                        {sortField === 'numeroLote' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('cantidad')}>
                      <div className="flex items-center gap-2">
                        Cantidad
                        {sortField === 'cantidad' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('autor')}>
                      <div className="flex items-center gap-2">
                        Autor
                        {sortField === 'autor' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('precioBase')}>
                      <div className="flex items-center gap-2">
                        Precio
                        {sortField === 'precioBase' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium">Ofertas</th>
                    <th className="text-left p-4 font-medium">Estado</th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('fechaLimiteOfertas')}>
                      <div className="flex items-center gap-2">
                        Fin Ofertas
                        {sortField === 'fechaLimiteOfertas' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('createdAt')}>
                      <div className="flex items-center gap-2">
                        Fecha Pub.
                        {sortField === 'createdAt' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-right p-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentDisplayLotes.map((lote) => (
                    <tr key={lote.id} className="border-b last:border-0 hover:bg-accent/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                            {lote.fotos && lote.fotos[0] ? (
                              <img src={getPhotoUrl(lote.fotos[0])} alt={lote.tipoGanado} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <FileText className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{lote.tipoGanado} {lote.categoria}</div>
                            <div className="text-sm text-muted-foreground">{lote.raza}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">{lote.numeroLote}</td>
                      <td className="p-4">{lote.cantidad.toLocaleString()}</td>
                      <td className="p-4 text-sm">{lote.autor}</td>
                      <td className="p-4 font-medium">{lote.precioBase ? `$${lote.precioBase.toLocaleString('en-US')}` : 'Sin Base'}</td>
                      <td className="p-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewOfertas(lote)}
                          className="gap-2"
                          data-testid="view-offers-button"
                        >
                          <TrendingUp className="h-4 w-4" />
                          {lote.ofertasCount || 0}
                        </Button>
                      </td>
                      <td className="p-4">
                        {isVencida(lote) ? (
                          <Badge variant="destructive">
                            Vencida
                          </Badge>
                        ) : (
                          <Badge variant={badgeVariants[lote.estadoPublicacion]}>
                            {lote.estadoPublicacion}
                          </Badge>
                        )}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                        {lote.fechaLimiteOfertas ? (
                          <div className="flex flex-col">
                            <span>{new Date(lote.fechaLimiteOfertas).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                            <span className="text-xs opacity-75">{new Date(lote.fechaLimiteOfertas).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })} hs</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(lote.createdAt).toLocaleDateString('es-UY', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <Button asChild variant="default" size="sm">
                            <Link to={`/publicaciones/${lote.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {lote.estadoPublicacion === 'Borrador' && (
                            <Button asChild variant="outline" size="sm">
                              <Link to={`/publicaciones/nueva?edit=${lote.id}`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {isSuperAdmin && (
                            <Button variant="outline" size="sm" onClick={() => setChangeAuthorDialog(lote.id)} title="Cambiar Autor">
                              <User className="h-4 w-4" />
                            </Button>
                          )}
                          <Button asChild variant="outline" size="sm">
                            <Link to={`/publicaciones/nueva?duplicar=${lote.id}`}>
                              <Copy className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setDeleteId(lote.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Pagination Controls */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalCount / pageSize)}
          onPageChange={setCurrentPage}
          className="pb-8"
        />
      </div>

      {/* Modal de Ofertas */}
      <Dialog open={!!selectedLoteOfertas} onOpenChange={() => setSelectedLoteOfertas(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Ofertas para {selectedLoteOfertas?.tipoGanado} {selectedLoteOfertas?.categoria}
            </DialogTitle>
            <DialogDescription>
              {selectedLoteOfertas?.numeroLote} • {selectedLoteOfertas?.cantidad?.toLocaleString()} cantidad •
              Precio Base: {selectedLoteOfertas?.precioBase ? `$${selectedLoteOfertas?.precioBase?.toLocaleString('en-US')}` : 'Sin Base'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {selectedLoteOfertas && (
              <>
                {ofertas.filter(o => o.loteId === selectedLoteOfertas.id).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay ofertas para este vehículo
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ofertas
                      .filter(o => o.loteId === selectedLoteOfertas.id)
                      .map((oferta) => {
                        const cliente = clientes[oferta.clienteId];
                        return (
                          <Card key={oferta.id} className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="font-medium text-lg">
                                  {cliente?.nombre || 'Cliente'}
                                </div>
                                {cliente?.empresa && (
                                  <div className="text-sm text-muted-foreground">
                                    {cliente.empresa}
                                  </div>
                                )}
                                {cliente?.whatsapp && (
                                  <div className="text-sm text-muted-foreground">
                                    📱 {cliente.whatsapp}
                                  </div>
                                )}
                              </div>
                              <Badge variant={badgeVariants[oferta.estado]}>
                                {oferta.estado}
                              </Badge>
                            </div>

                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-2xl font-bold text-primary">
                                  ${oferta.monto.toLocaleString('en-US')}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {new Date(oferta.hora).toLocaleString('es-UY', {
                                    dateStyle: 'short',
                                    timeStyle: 'short'
                                  })}
                                </div>
                              </div>

                              {oferta.estado === 'Pendiente' && selectedLoteOfertas.estadoPublicacion !== 'Adjudicada' && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => handleAcceptOffer(oferta.id)}
                                    disabled={!!processingOffer}
                                    data-testid="modal-accept-offer-button"
                                  >
                                    {processingOffer === oferta.id ? (
                                      <><div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-1" />Procesando...</>
                                    ) : (
                                      <><Check className="h-4 w-4 mr-1" />Aceptar</>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRejectOffer(oferta.id)}
                                    disabled={!!processingOffer}
                                    data-testid="modal-reject-offer-button"
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Rechazar
                                  </Button>
                                </div>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Cambio de Autor */}
      <Dialog open={!!changeAuthorDialog} onOpenChange={(open) => {
        if (!open) {
          setChangeAuthorDialog(null);
          setSelectedNewAuthor('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar Autor de la Publicación</DialogTitle>
            <DialogDescription>
              Selecciona un nuevo autor de la lista de usuarios. El autor previo será reemplazado.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedNewAuthor} onValueChange={setSelectedNewAuthor}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un autor..." />
              </SelectTrigger>
              <SelectContent>
                {usuarios.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.nombre} ({u.rol})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeAuthorDialog(null)}>Cancelar</Button>
            <Button onClick={handleChangeAuthor} disabled={!selectedNewAuthor}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar publicación?</AlertDialogTitle>
            <AlertDialogDescription>
              La publicación será movida a la papelera. Podrás restaurarla más adelante si lo necesitás.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete Confirmation Dialog */}
      <AlertDialog open={!!permanentDeleteId} onOpenChange={() => setPermanentDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción es irreversible. La publicación y todas sus ofertas serán eliminadas permanentemente de la base de datos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handlePermanentDelete(permanentDeleteId)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Publicaciones;